import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

export interface ScrollToInvalidOptions {
    /**
     * Offset from the top of the viewport (useful for sticky headers).
     * Default: 120px
     */
    scrollOffset?: number;

    /**
     * Scroll behavior: 'smooth' or 'auto'
     * Default: 'smooth'
     */
    scrollBehavior?: ScrollBehavior;

    /**
     * Whether to focus the invalid control after scrolling
     * Default: true
     */
    focusInvalidControl?: boolean;

    /**
     * Container element to search within (defaults to document.body)
     */
    container?: HTMLElement;
}

/**
 * Programmatically scroll to the first invalid control in a form.
 * This function can be called from component code without using the directive.
 *
 * @param formGroup The form group to check for invalid controls
 * @param options Scroll options
 * @returns true if scrolled to an invalid control, false otherwise
 *
 * @example
 * ```typescript
 * onSubmit() {
 *   if (this.form.invalid) {
 *     scrollToInvalidControl(this.form);
 *     return;
 *   }
 *   // proceed with submission
 * }
 * ```
 */
export function scrollToInvalidControl(
    formGroup: FormGroup | FormArray,
    options: ScrollToInvalidOptions = {}
): boolean {
    const {
        scrollOffset = 120,
        scrollBehavior = 'smooth',
        focusInvalidControl = true,
        container = document.body,
    } = options;

    if (!formGroup || formGroup.valid) {
        return false;
    }

    // Mark all as touched to show validation errors
    formGroup.markAllAsTouched();

    // Find the first invalid control
    const firstInvalidControl = findFirstInvalidControl(formGroup);
    if (!firstInvalidControl) {
        console.warn('scrollToInvalidControl: No invalid control found');
        return false;
    }

    // Find the DOM element
    const invalidElement = findInvalidElement(firstInvalidControl, container);
    if (!invalidElement) {
        console.warn('scrollToInvalidControl: Could not find DOM element for invalid control');
        return false;
    }

    // Scroll to the element
    scrollToElement(invalidElement, scrollOffset, scrollBehavior);

    // Optionally focus the element
    if (focusInvalidControl) {
        setTimeout(() => {
            const focusableElement = getFocusableElement(invalidElement);
            focusableElement?.focus();
        }, 100);
    }

    return true;
}

/**
 * Recursively find the first invalid control in the form tree (depth-first)
 */
function findFirstInvalidControl(
    control: AbstractControl,
    path: string = ''
): { control: AbstractControl; path: string } | null {
    if (control.invalid) {
        // If it's a leaf control (not a group/array), return it
        if (!(control instanceof FormGroup) && !(control instanceof FormArray)) {
            return { control, path };
        }

        // If it's a group or array, recurse into children
        if (control instanceof FormGroup) {
            const controls = control.controls;
            for (const key of Object.keys(controls)) {
                const childControl = controls[key];
                const childPath = path ? `${path}.${key}` : key;
                const result = findFirstInvalidControl(childControl, childPath);
                if (result) {
                    return result;
                }
            }
        } else if (control instanceof FormArray) {
            const controls = control.controls;
            for (let i = 0; i < controls.length; i++) {
                const childControl = controls[i];
                const childPath = path ? `${path}[${i}]` : `[${i}]`;
                const result = findFirstInvalidControl(childControl, childPath);
                if (result) {
                    return result;
                }
            }
        }

        // If we couldn't find a leaf control, return this group/array
        return { control, path };
    }

    return null;
}

/**
 * Find the DOM element associated with the invalid control
 */
function findInvalidElement(
    invalidControlInfo: { control: AbstractControl; path: string },
    container: HTMLElement
): HTMLElement | null {
    // Strategy 1: Try to find by formControlName or formArrayName
    const pathParts = invalidControlInfo.path.split('.').filter((p) => !p.includes('['));
    for (const part of pathParts) {
        const element = container.querySelector(`[formControlName="${part}"]`) as HTMLElement;
        if (element) {
            return element;
        }
    }

    // Strategy 2: Try to find by ng-invalid class (Angular adds this automatically)
    const invalidElements = container.querySelectorAll('.ng-invalid');
    // Find the first one that's a form field (input, select, textarea)
    for (let i = 0; i < invalidElements.length; i++) {
        const element = invalidElements[i] as HTMLElement;
        if (isFormField(element)) {
            return element;
        }
    }

    // Strategy 3: Return the first ng-invalid element
    if (invalidElements.length > 0) {
        return invalidElements[0] as HTMLElement;
    }

    return null;
}

/**
 * Check if element is a form field
 */
function isFormField(element: HTMLElement): boolean {
    const tagName = element.tagName.toLowerCase();
    return (
        ['input', 'select', 'textarea', 'mds-input', 'mds-select', 'mds-datepicker', 'mds-dropdown'].includes(
            tagName
        ) ||
        element.hasAttribute('formControlName') ||
        element.hasAttribute('formArrayName')
    );
}

/**
 * Scroll to the element with offset
 */
function scrollToElement(element: HTMLElement, scrollOffset: number, scrollBehavior: ScrollBehavior): void {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - scrollOffset;

    window.scrollTo({
        top: offsetPosition,
        behavior: scrollBehavior,
    });
}

/**
 * Get the actual focusable element (might be nested inside the element)
 */
function getFocusableElement(element: HTMLElement): HTMLElement | null {
    // If the element itself is focusable, return it
    if (isFocusable(element)) {
        return element;
    }

    // Try to find a focusable child
    const focusableChild = element.querySelector(
        'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
    ) as HTMLElement;

    return focusableChild || element;
}

/**
 * Check if element is focusable
 */
function isFocusable(element: HTMLElement): boolean {
    return (
        element.tabIndex >= 0 && !element.hasAttribute('disabled') && element.offsetParent !== null // visible check
    );
}
